import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Subject, bufferTime, filter } from "rxjs";
import { Url } from "../Url";
import { HttpContextToken } from "@angular/common/http";

export type TelemetryLevel = "debug" | "info" | "warn" | "error";

export interface TelemetryEntry {
  level: TelemetryLevel;
  /** Stable identifier for the kind of event, e.g. "et_gap". Filtered on in CloudWatch. */
  event: string;
  /** Optional human-readable message. */
  message?: string;
  /** Arbitrary structured context. Keep small - the request body cap is ~100KB. */
  context?: Record<string, unknown>;
  /** Page URL the event originated from. Auto-filled from `window.location.href` if omitted. */
  url?: string;
}

interface ClientLogBatch {
  entries: TelemetryEntry[];
}

const FLUSH_INTERVAL_MS = 500;
const MAX_BATCH_SIZE = 50; // matches ClientLogBatchDto.@ArrayMaxSize on the backend

/**
 * Forwards structured client-side log entries to the backend, where they're
 * re-emitted through pino and end up in CloudWatch alongside server logs.
 *
 * Calls are buffered for ~500ms (or until 50 entries accumulate) so a tight
 * loop of `warn(...)` calls doesn't fan out to one HTTP request each.
 *
 * Failures are intentionally swallowed: telemetry must never break the page.
 */
@Injectable({ providedIn: "root" })
export class TelemetryService {
  private readonly endpoint = `${Url}/telemetry/client-log`;
  private readonly queue$ = new Subject<TelemetryEntry>();

  constructor(private readonly http: HttpClient) {
    this.queue$
      .pipe(
        // (timeSpan, creationInterval, maxBufferSize)
        bufferTime(FLUSH_INTERVAL_MS, null, MAX_BATCH_SIZE),
        filter((entries) => entries.length > 0),
      )
      .subscribe((entries) => this.flush(entries));
  }

  log(entry: TelemetryEntry): void {
    this.queue$.next({
      ...entry,
      url: entry.url ?? window.location.href,
    });
  }

  // Convenience wrappers - keep call sites short.
  debug(event: string, opts?: Omit<TelemetryEntry, "level" | "event">): void {
    this.log({ level: "debug", event, ...opts });
  }
  info(event: string, opts?: Omit<TelemetryEntry, "level" | "event">): void {
    this.log({ level: "info", event, ...opts });
  }
  warn(event: string, opts?: Omit<TelemetryEntry, "level" | "event">): void {
    this.log({ level: "warn", event, ...opts });
  }
  error(event: string, opts?: Omit<TelemetryEntry, "level" | "event">): void {
    this.log({ level: "error", event, ...opts });
  }

  private flush(entries: TelemetryEntry[]): void {
    const body: ClientLogBatch = { entries };
    const context = new HttpContext().set(SILENT_HTTP, true);
    this.http
      .post(this.endpoint, body, { context })
      .subscribe({ error: () => undefined });
  }
}

/**
 * When set to `true` on an outgoing request's HttpContext, the global HTTP
 * interceptors will treat it as a silent best-effort call:
 *   - the auth interceptor will not force-logout on a 401, and
 *   - the error interceptor will not show snackbars/dialogs for failures.
 *
 * Use this for fire-and-forget telemetry / metrics requests — anything where
 * a failure is something we want logged silently, not surfaced to the user.
 */
export const SILENT_HTTP = new HttpContextToken<boolean>(() => false);
